import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications } from '@/api/notifications';
import { Button } from '@/components/ui/button';
import { Bell, Trash2, ChevronDown, ChevronUp, Link as LinkIcon, CheckCheck } from 'lucide-react';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { resolveNotificationLink } from '@/utils/linkResolver';
import { cn } from '@/lib/utils';

/**
 * Component to display user notification history
 */
export default function NotificationList({ onClose, onRefreshCount }) {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextUrl, setNextUrl] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const fetchNotifications = async (url = null) => {
        if (url) setLoadingMore(true);
        else setLoading(true);

        try {
            const res = await getNotifications(url);
            const data = res.data;
            
            if (url) {
                setNotifications(prev => [...prev, ...(data.results || [])]);
            } else {
                setNotifications(data.results || data || []);
            }
            
            setNextUrl(data.next || null);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const handleMarkRead = async (id) => {
        try {
            await markAsRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            if (onRefreshCount) onRefreshCount();
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    };

    const handleNotificationClick = async (n) => {
        // Mark as read immediately for UX
        if (!n.is_read) {
            handleMarkRead(n.id);
        }

        // Try to find a link in the data
        const targetUrl = n.data?.url || n.data?.link;
        if (targetUrl) {
            resolveNotificationLink(targetUrl, navigate);
            if (onClose) onClose();
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            if (onRefreshCount) onRefreshCount();
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteNotification(id);
            setNotifications(prev => prev.filter(n => n.id !== id));
            if (onRefreshCount) onRefreshCount();
        } catch (err) {
            console.error('Failed to delete notification:', err);
        }
    };

    const confirmDeleteAll = async () => {
        try {
            await deleteAllNotifications();
            setNotifications([]);
            if (onRefreshCount) onRefreshCount();
        } catch (err) {
            console.error('Failed to delete all notifications:', err);
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    const toggleExpand = (e, id) => {
        e.stopPropagation(); // Avoid triggering the main click
        setExpandedId(expandedId === id ? null : id);
    };

    return (
        <div className="flex flex-col h-full max-h-[600px] w-full sm:w-[380px] bg-white dark:bg-[#0F1F1A] border border-gray-200 dark:border-white/10 shadow-2xl rounded-2xl overflow-hidden pointer-events-auto">
            {/* Header */}
            <div className="p-4 border-b border-gray-100 dark:border-white/5 flex items-center justify-between bg-white dark:bg-white/[0.04] sticky top-0 z-10">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-primary/20 dark:bg-primary/20 rounded-xl shadow-sm border border-primary/10">
                        <Bell className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-black text-sm text-gray-900 dark:text-white tracking-tight">Notifications</h3>
                </div>
                <div className="flex items-center gap-1.5">
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleMarkAllRead} 
                        disabled={!notifications.some(n => !n.is_read)}
                        className="h-8 w-8 p-0 rounded-full text-primary hover:bg-primary hover:text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed border border-primary/10"
                        title="Mark all as read"
                    >
                        <CheckCheck className="w-4 h-4" />
                    </Button>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowDeleteConfirm(true)} 
                        disabled={notifications.length === 0}
                        className="h-8 w-8 p-0 rounded-full text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed border border-primary/10"
                        title="Delete all notifications"
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {loading ? (
                    <div className="p-8 text-center animate-pulse">
                        <div className="w-8 h-8 bg-gray-200 dark:bg-white/10 rounded-full mx-auto mb-2" />
                        <div className="h-2 bg-gray-100 dark:bg-white/5 rounded w-24 mx-auto" />
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="p-16 text-center">
                        <div className="relative w-20 h-20 mx-auto mb-6 bg-primary/5 dark:bg-white/5 rounded-full flex items-center justify-center">
                            <Bell className="w-10 h-10 text-primary/30 animate-pulse" />
                            <div className="absolute inset-0 bg-primary/5 rounded-full blur-xl" />
                        </div>
                        <p className="text-base font-black text-gray-900 dark:text-white">All caught up!</p>
                        <p className="text-sm mt-2 text-gray-600 dark:text-gray-400 max-w-[200px] mx-auto leading-relaxed">
                            You'll see updates here when they arrive.
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100 dark:divide-white/5">
                        <AnimatePresence initial={false}>
                            {notifications.map((n) => {
                                const isExpanded = expandedId === n.id;
                                const isLong = n.body && n.body.length > 80;
                                const hasLink = !!(n.data?.url || n.data?.link);

                                return (
                                    <motion.div
                                        key={n.id}
                                        layout
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        onClick={() => handleNotificationClick(n)}
                                        className={cn(
                                            "px-3 py-2 transition-all relative group border-l-4 cursor-pointer",
                                            !n.is_read 
                                            ? "bg-[hsl(var(--primary))]/[0.04] dark:bg-primary/10 border-primary hover:bg-[hsl(var(--primary))]/[0.08]" 
                                            : "bg-transparent border-transparent hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                                        )}
                                    >
                                        <div className="flex justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className={cn(
                                                        "text-sm font-bold leading-tight",
                                                        !n.is_read ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-white/40"
                                                    )}>
                                                        {n.title}
                                                    </p>
                                                </div>
                                                <p className={cn(
                                                    "text-xs mt-1 leading-relaxed transition-all duration-300 whitespace-pre-line",
                                                    !n.is_read ? "text-gray-700 dark:text-gray-200" : "text-gray-400 dark:text-white/30",
                                                    !isExpanded && "line-clamp-2"
                                                )}>
                                                    {n.body}
                                                </p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <p className="text-[10px] text-gray-400 dark:text-white/30 font-bold uppercase tracking-wider">
                                                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                                                    </p>
                                                    {!n.is_read && (
                                                        <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-center shrink-0 self-stretch justify-between">
                                                <div className="flex flex-col gap-2 items-center">
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(n.id); }} 
                                                        className="p-1.5 text-gray-400 hover:text-primary-foreground hover:bg-primary rounded-full bg-white dark:bg-white/10 shadow-sm border border-gray-100 dark:border-transparent transition-all" 
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>

                                                    {hasLink && (
                                                        <div className="p-1 text-primary/60" title="Has link">
                                                            <LinkIcon className="w-3.5 h-3.5" />
                                                        </div>
                                                    )}
                                                </div>

                                                {isLong && (
                                                    <button 
                                                        onClick={(e) => toggleExpand(e, n.id)} 
                                                        className="p-1.5 text-primary/60 hover:bg-primary hover:text-white rounded-full bg-white dark:bg-white/10 shadow-sm border border-gray-100 dark:border-transparent transition-all"
                                                        title={isExpanded ? "Collapse" : "Expand"}
                                                    >
                                                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>

                        {/* Load More Button */}
                        {nextUrl && (
                            <div className="p-4 text-center bg-gray-50/50 dark:bg-transparent">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={(e) => { e.stopPropagation(); fetchNotifications(nextUrl); }}
                                    disabled={loadingMore}
                                    className="w-full text-[11px] font-black uppercase tracking-widest border-primary/20 text-primary hover:bg-primary hover:text-white transition-all rounded-xl h-10"
                                >
                                    {loadingMore ? 'Loading...' : 'Load More Results'}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ConfirmationModal 
                isOpen={showDeleteConfirm}
                onOpenChange={setShowDeleteConfirm}
                title="Delete All Notifications"
                description="Are you sure you want to permanently delete all your notifications? This action cannot be undone."
                confirmText="Yes, Delete All"
                cancelText="Cancel"
                onConfirm={confirmDeleteAll}
                variant="default"
                icon={Trash2}
                zIndex="z-[20000]"
            />
        </div>
    );
}
